import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { fn, hash } from "@ember/helper";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import Form from "discourse/components/form";
import { extractError } from "discourse/lib/ajax-error";
import { INVITE_DESCRIPTION_MAX_LENGTH } from "discourse/lib/constants";
import { canNativeShare, nativeShare } from "discourse/lib/pwa-utils";
import { sanitize } from "discourse/lib/text";
import { applyValueTransformer } from "discourse/lib/transformer";
import { emailValid, hostnameValid } from "discourse/lib/utilities";
import Invite from "discourse/models/invite";
import { FORMAT as DATE_INPUT_FORMAT } from "discourse/select-kit/components/future-date-input-selector";
import GroupChooser from "discourse/select-kit/components/group-chooser";
import TopicChooser from "discourse/select-kit/components/topic-chooser";
import { and, notEq, or } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DCopyButton from "discourse/ui-kit/d-copy-button";
import DFutureDateInput from "discourse/ui-kit/d-future-date-input";
import DModal from "discourse/ui-kit/d-modal";
import I18n, { i18n } from "discourse-i18n";

export default class CreateInvite extends Component {
  @service currentUser;
  @service siteSettings;
  @service site;
  @service appEvents;

  @tracked saving = false;
  @tracked displayAdvancedOptions = false;
  @tracked isEmailInvite = emailValid(this.data.restrictTo);

  @tracked flashText;
  @tracked flashClass = "info";

  @tracked topics = this.invite.topics ?? this.model.topics ?? [];
  allGroups = this.site.groups.filter((g) => !g.automatic);

  model = this.args.model;
  invite = this.model.invite ?? Invite.create();
  sendEmail = false;
  focusFormOnInsert = false;
  focusLinkAfterCreate = false;
  formApi;

  get linkValidityMessageFormat() {
    return I18n.messageFormat("user.invited.invite.link_validity_MF", {
      user_count: this.defaultRedemptionsAllowed,
      duration_days: this.siteSettings.invite_expiry_days,
    });
  }

  get expireAfterOptions() {
    let list = [1, 7, 30, 90];

    if (!list.includes(this.siteSettings.invite_expiry_days)) {
      list.push(this.siteSettings.invite_expiry_days);
    }

    list = list
      .sort((a, b) => a - b)
      .map((days) => {
        return {
          value: days,
          text: i18n("dates.medium.x_days", { count: days }),
        };
      });

    list.push({
      value: 999999,
      text: i18n("time_shortcut.never"),
    });

    return list;
  }

  @cached
  get data() {
    const data = {
      description: this.invite.description ?? "",
      restrictTo: this.invite.emailOrDomain ?? "",
      maxRedemptions:
        this.invite.max_redemptions_allowed ?? this.defaultRedemptionsAllowed,
      inviteToTopic: this.invite.topicId,
      inviteToGroups: this.model.groupIds ?? this.invite.groupIds ?? [],
      customMessage: this.invite.custom_message ?? "",
    };

    if (this.inviteCreated) {
      data.expiresAt = this.invite.expires_at;
    } else {
      data.expiresAfterDays = this.siteSettings.invite_expiry_days;
    }

    return data;
  }

  get descriptionValidation() {
    return `length:0,${INVITE_DESCRIPTION_MAX_LENGTH}`;
  }

  get maxRedemptionsAllowedLimit() {
    if (this.currentUser.staff) {
      return this.siteSettings.invite_link_max_redemptions_limit;
    }

    return this.siteSettings.invite_link_max_redemptions_limit_users;
  }

  get defaultRedemptionsAllowed() {
    const max = this.maxRedemptionsAllowedLimit;
    const val = this.currentUser.staff ? 100 : 10;
    return Math.min(max, val);
  }

  get canInviteToGroup() {
    return (
      this.currentUser.staff ||
      this.currentUser.visibleGroups.some((g) => g.group_user?.owner)
    );
  }

  get canArriveAtTopic() {
    return this.currentUser.staff && !this.siteSettings.must_approve_users;
  }

  get canSendEmailInvite() {
    return this.isEmailInvite && this.siteSettings.allow_email_invites;
  }

  get linkOptionsLabel() {
    return this.siteSettings.allow_email_invites
      ? i18n("user.invited.invite.edit_link_options")
      : i18n("user.invited.invite.edit_link_options_only");
  }

  get simpleMode() {
    return !this.args.model.editing && !this.displayAdvancedOptions;
  }

  get inviteCreated() {
    return !!this.invite.get("id");
  }

  async save(data) {
    let isLink = true;
    const restrictTo = data.emailOrDomain;
    delete data.emailOrDomain;

    if (restrictTo) {
      if (this.isEmailInvite) {
        isLink = false;
        data.email = restrictTo;
      } else if (hostnameValid(restrictTo)) {
        data.domain = restrictTo;
      }
    }

    if (isLink) {
      if (this.invite.email) {
        data.email = data.custom_message = "";
      }

      // the server only touches the column when the key is present, so an
      // emptied field has to send a blank value to drop the restriction
      if (!restrictTo && this.invite.domain) {
        data.domain = "";
      }
    } else {
      if (data.max_redemptions_allowed > 1) {
        data.max_redemptions_allowed = 1;
      }

      if (this.sendEmail) {
        data.send_email = true;
        if (data.topic_id) {
          data.invite_to_topic = true;
        }
      } else {
        data.skip_email = true;
      }
    }

    this.saving = true;
    const wasInviteCreated = this.inviteCreated;
    try {
      await this.invite.save(data);
      if (!wasInviteCreated && this.inviteCreated) {
        this.focusLinkAfterCreate = true;
      }
      const invites = this.model?.invites;
      if (invites && !invites.some((i) => i.id === this.invite.id)) {
        invites.unshift(this.invite);
      }

      if (!this.simpleMode) {
        if (this.sendEmail) {
          this.flashText = sanitize(
            i18n("user.invited.invite.invite_saved_with_sending_email")
          );
        } else {
          this.flashText = sanitize(
            i18n("user.invited.invite.invite_saved_without_sending_email")
          );
        }
        this.flashClass = "success";
      }
      this.appEvents.trigger("create-invite:saved", this.invite);
    } catch (error) {
      this.flashText = sanitize(extractError(error));
      this.flashClass = "error";
    } finally {
      this.saving = false;
    }
  }

  @action
  handleRestrictToChange(value, { set }) {
    set("restrictTo", value);
    this.isEmailInvite = emailValid(value);
  }

  @action
  async onFormSubmit(data) {
    const submitData = {
      description: data.description,
      emailOrDomain: data.restrictTo?.trim(),
      group_ids: data.inviteToGroups,
      topic_id: data.inviteToTopic,
      max_redemptions_allowed: data.maxRedemptions,
      custom_message: data.customMessage,
    };

    if (data.expiresAt) {
      submitData.expires_at = data.expiresAt;
    } else if (data.expiresAfterDays) {
      submitData.expires_at = moment()
        .add(data.expiresAfterDays, "days")
        .format(DATE_INPUT_FORMAT);
    }

    await this.save(submitData);
  }

  @action
  saveInvite() {
    this.sendEmail = false;
    this.formApi.submit();
  }

  @action
  saveInviteAndSendEmail() {
    this.sendEmail = true;
    this.formApi.submit();
  }

  @action
  onChangeTopic(fieldSet, topicId, topic) {
    this.topics = [topic];
    fieldSet(topicId);
  }

  @action
  showAdvancedMode() {
    this.focusFormOnInsert = true;
    this.displayAdvancedOptions = true;
  }

  @action
  maybeFocusFirstField(element) {
    if (this.focusFormOnInsert) {
      this.focusFormOnInsert = false;
      element.querySelector(".form-kit__control-input")?.focus();
    }
  }

  @action
  maybeFocusCopyButton(element) {
    if (this.focusLinkAfterCreate) {
      this.focusLinkAfterCreate = false;
      element.querySelector("button")?.focus();
    }
  }

  @action
  async createLink() {
    this.sendEmail = false;

    const topicId = applyValueTransformer("invite-simple-mode-topic", null, {
      invite: this.invite,
    });

    const groupIds = this.data.inviteToGroups;

    await this.save({
      max_redemptions_allowed: this.defaultRedemptionsAllowed,
      expires_at: moment()
        .add(this.siteSettings.invite_expiry_days, "days")
        .format(DATE_INPUT_FORMAT),
      ...(topicId != null && { topic_id: topicId }),
      ...(groupIds.length > 0 && { group_ids: groupIds }),
    });
  }

  @action
  cancel() {
    this.args.closeModal();
  }

  @action
  registerApi(api) {
    this.formApi = api;
  }

  <template>
    <DModal
      class="create-invite-modal"
      @closeModal={{@closeModal}}
      @hideFooter={{and this.simpleMode this.inviteCreated}}
      @inline={{@inline}}
      @title={{i18n
        (if
          @model.editing
          "user.invited.invite.edit_title"
          "user.invited.invite.new_title"
        )
      }}
    >
      <:belowHeader>
        {{#if (or this.flashText @model.editing)}}
          <InviteModalAlert
            @alertClass={{this.flashClass}}
            @invite={{this.invite}}
            @onLinkInsert={{this.maybeFocusCopyButton}}
            @showInviteLink={{and
              this.inviteCreated
              (notEq this.flashClass "error")
            }}
          >
            {{#if this.flashText}}
              {{trustHTML this.flashText}}
            {{else}}
              {{i18n "user.invited.invite.copy_link_and_share_it"}}
            {{/if}}
          </InviteModalAlert>
        {{/if}}
      </:belowHeader>
      <:body>
        {{#if this.simpleMode}}
          {{#if this.inviteCreated}}
            {{#unless this.site.mobileView}}
              <p>
                {{i18n "user.invited.invite.copy_link_and_share_it"}}
              </p>
            {{/unless}}
            <div
              class="link-share-container"
              {{didInsert this.maybeFocusCopyButton}}
            >
              <ShareOrCopyInviteLink @invite={{this.invite}} />
            </div>
          {{else}}
            <p>
              {{i18n "user.invited.invite.create_link_to_invite"}}
            </p>
          {{/if}}
          <p class="link-limits-info">
            {{this.linkValidityMessageFormat}}
          </p>
          <p>
            <DButton
              class="edit-link-options"
              @action={{this.showAdvancedMode}}
              @display="link"
              @translatedLabel={{this.linkOptionsLabel}}
            />
          </p>
        {{else}}
          <Form
            @data={{this.data}}
            @onRegisterApi={{this.registerApi}}
            @onSubmit={{this.onFormSubmit}}
            {{didInsert this.maybeFocusFirstField}}
            as |form|
          >
            <form.Field
              @description={{i18n "user.invited.invite.description_help"}}
              @format="full"
              @name="description"
              @title={{i18n "user.invited.invite.description"}}
              @type="input"
              @validation={{this.descriptionValidation}}
              as |field|
            >
              <field.Control />
            </form.Field>
            <form.Field
              @description={{i18n "user.invited.invite.restrict_help"}}
              @format="full"
              @name="restrictTo"
              @onSet={{this.handleRestrictToChange}}
              @title={{i18n "user.invited.invite.restrict"}}
              @type="input"
              as |field|
            >
              <field.Control
                placeholder={{i18n
                  "user.invited.invite.email_or_domain_placeholder"
                }}
              />
            </form.Field>

            {{#unless this.isEmailInvite}}
              <form.Field
                @format="small"
                @name="maxRedemptions"
                @title={{i18n "user.invited.invite.max_redemptions_allowed"}}
                @type="input-number"
                @validation="required"
                as |field|
              >
                <field.Control
                  max={{this.maxRedemptionsAllowedLimit}}
                  min="1"
                />
              </form.Field>
            {{/unless}}

            {{#if this.inviteCreated}}
              <form.Field
                @format="full"
                @name="expiresAt"
                @title={{i18n "user.invited.invite.expires_at"}}
                @type="custom"
                @validation="required"
                as |field|
              >
                <field.Control>
                  <DFutureDateInput
                    @clearable={{true}}
                    @input={{field.value}}
                    @noRelativeOptions={{true}}
                    @onChangeInput={{field.set}}
                  />
                </field.Control>
              </form.Field>
            {{else}}
              <form.Field
                @format="full"
                @name="expiresAfterDays"
                @title={{i18n "user.invited.invite.expires_after"}}
                @type="select"
                @validation="required"
                as |field|
              >
                <field.Control as |select|>
                  {{#each this.expireAfterOptions as |option|}}
                    <select.Option
                      @value={{option.value}}
                    >{{option.text}}</select.Option>
                  {{/each}}
                </field.Control>
              </form.Field>
            {{/if}}

            {{#if this.canArriveAtTopic}}
              <form.Field
                @format="full"
                @name="inviteToTopic"
                @title={{i18n "user.invited.invite.invite_to_topic"}}
                @type="custom"
                as |field|
              >
                <field.Control>
                  <TopicChooser
                    @content={{this.topics}}
                    @onChange={{fn this.onChangeTopic field.set}}
                    @options={{hash additionalFilters="status:public"}}
                    @value={{field.value}}
                  />
                </field.Control>
              </form.Field>
            {{/if}}

            {{#if this.canInviteToGroup}}
              <form.Field
                @format="full"
                @name="inviteToGroups"
                @title={{i18n "user.invited.invite.add_to_groups"}}
                @type="custom"
                as |field|
              >
                <field.Control>
                  <GroupChooser
                    @content={{this.allGroups}}
                    @labelProperty="name"
                    @onChange={{field.set}}
                    @value={{field.value}}
                  />
                </field.Control>
              </form.Field>
            {{/if}}

            {{#if this.canSendEmailInvite}}
              <form.Field
                @description={{i18n "user.invited.invite.custom_message_help"}}
                @format="full"
                @name="customMessage"
                @title={{i18n "user.invited.invite.custom_message"}}
                @type="textarea"
                as |field|
              >
                <field.Control
                  height={{100}}
                  placeholder={{i18n
                    "user.invited.invite.custom_message_placeholder"
                  }}
                />
              </form.Field>
            {{/if}}
          </Form>
        {{/if}}
      </:body>
      <:footer>
        {{#if this.simpleMode}}
          <DButton
            autofocus="true"
            class="btn-primary save-invite"
            @action={{this.createLink}}
            @disabled={{this.saving}}
            @label="user.invited.invite.create_link"
          />
        {{else}}
          <DButton
            class="btn-primary save-invite"
            @action={{this.saveInvite}}
            @disabled={{this.saving}}
            @label={{if
              this.inviteCreated
              "user.invited.invite.update_invite"
              "user.invited.invite.create_link"
            }}
          />
          {{#if this.canSendEmailInvite}}
            <DButton
              autofocus="true"
              class="btn-primary save-invite-and-send-email"
              @action={{this.saveInviteAndSendEmail}}
              @disabled={{this.saving}}
              @label={{if
                this.inviteCreated
                "user.invited.invite.update_invite_and_send_email"
                "user.invited.invite.create_link_and_send_email"
              }}
            />
          {{/if}}
        {{/if}}
        <DButton
          class="btn-transparent cancel-button"
          @action={{this.cancel}}
          @label="user.invited.invite.cancel"
        />
      </:footer>
    </DModal>
  </template>
}

const InviteModalAlert = <template>
  <div
    class="alert alert-{{@alertClass}}"
    id="modal-alert"
    role={{if (notEq @alertClass "error") "status" "alert"}}
  >
    <div class="input-group invite-link">
      <label for="invite-link">
        {{yield}}
      </label>
      {{#if @showInviteLink}}
        <div class="link-share-container" {{didInsert @onLinkInsert}}>
          <ShareOrCopyInviteLink @invite={{@invite}} />
        </div>
      {{/if}}
    </div>
  </div>
</template>;

class ShareOrCopyInviteLink extends Component {
  @service capabilities;

  @action
  async nativeShare() {
    await nativeShare(this.capabilities, { url: this.args.invite.link });
  }

  <template>
    <input
      class="invite-link"
      name="invite-link"
      readonly={{true}}
      type="text"
      value={{@invite.link}}
    />
    {{#if (canNativeShare this.capabilities)}}
      <DButton
        class="btn-primary"
        @action={{this.nativeShare}}
        @icon="share"
        @translatedLabel={{i18n "user.invited.invite.share_link"}}
      />
    {{else}}
      <DCopyButton
        @selector="input.invite-link"
        @translatedLabel={{i18n "user.invited.invite.copy_link"}}
        @translatedLabelAfterCopy={{i18n "user.invited.invite.link_copied"}}
      />
    {{/if}}
  </template>
}
